import React, { useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';

// Actions
import * as ReportActions from 'store/ducks/reports/actions';
import * as UsersActions from 'store/ducks/users/actions';

// Types
import { ApplicationState } from 'store';
import { WebUser } from 'store/ducks/users/types';

// Helpers
import { useDebouncedSearch } from 'helpers/debounceHook';
import { promiseDispatch } from 'store/utils/promiseAction';
import {
  getCurrentSearchTerm,
  setCurrentSearchTerm,
} from 'helpers/tableFilters';

// Components
import { SearchAsyncComponent } from 'components/SearchAsyncComponent';

// Styles
import useStyles from './supervisorFilter.styles';

const storegeLimit =
  JSON.parse(window.localStorage.getItem('persist:root') as string)
    ?.localLimit || '10';
const { localLimit } = JSON.parse(storegeLimit) || 10;

async function searchSupervisor(filter: string) {
  if (getCurrentSearchTerm() && filter.trim()) {
    return promiseDispatch(
      UsersActions.filterWebUsersRequest({
        name: `%${filter}%`,
        _limit: localLimit,
      }),
    );
  }
  return false;
}

const useSearchSupervisor = () => useDebouncedSearch(searchSupervisor);

const SupervisorFilter: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const initialSupervisorsList = useSelector(
    (state: ApplicationState) => state.users.webUsersList || [],
  );

  const filteredSupervisors = useSelector(
    (state: ApplicationState) => state.reports.selectedSupervisors,
  );

  const [supervisorList, setSupervisorList] = useState<WebUser[]>([]);

  const [
    autoCompleteText,
    setAutoCompleteText,
    searchResults,
  ] = useSearchSupervisor();

  useEffect(() => {
    dispatch(UsersActions.listWebUsersRequest({}));
  }, [dispatch]);

  useEffect(() => {
    if (initialSupervisorsList.length > 0) {
      setSupervisorList(initialSupervisorsList);
    }
  }, [initialSupervisorsList]);

  const handleSupervisor = (supervisor: WebUser) => {
    const updated = [...filteredSupervisors];
    const index = filteredSupervisors.findIndex((e) => e.id === supervisor.id);

    if (index === -1) {
      updated.push({ id: supervisor.id, name: supervisor.name });
    } else updated.splice(index, 1);

    dispatch(
      ReportActions.setSelectedSupervisors({
        selectedSupervisors: updated,
      }),
    );
  };

  return (
    <Box>
      <SearchAsyncComponent
        placeholder='Digite o nome'
        loading={searchResults.loading}
        classes={{
          root: classes.root,
        }}
        inputSize='small'
        onChange={(text: string) => {
          setCurrentSearchTerm(text);
          setAutoCompleteText(text);
          if (!text) {
            dispatch(
              UsersActions.listWebUsersRequest({
                filtering: true,
              }),
            );
          }
        }}
        value={autoCompleteText}
      />
      <Box marginTop={1}>
        <List dense>
          {Array.isArray(supervisorList) &&
            supervisorList.map((supervisor) => (
              <ListItem key={supervisor.id} button>
                <ListItemText
                  id={supervisor.id}
                  primary={supervisor.name}
                  onClick={() => handleSupervisor(supervisor)}
                  classes={{ root: classes.itemText }}
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge='end'
                    color='primary'
                    onChange={() => handleSupervisor(supervisor)}
                    checked={
                      !!filteredSupervisors.find((e) => e.id === supervisor.id)
                    }
                    inputProps={{ 'aria-labelledby': supervisor.id }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
        </List>
      </Box>
    </Box>
  );
};

export { SupervisorFilter };
export default SupervisorFilter;
